import React, { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
} from '@mui/material';
import { useAppSettings } from '../../hooks/useAppSettings';
import {
  QrCode as SkuIcon,
  LibraryAdd as LibraryAddIcon,
  Inventory2 as InventoryDashboardIcon,
  PhotoLibrary as ImageProcessingIcon,
  CloudQueue as WebhookIcon,
} from '@mui/icons-material';
import NextSkuGenerator from './NextSkuGenerator';
import ImageProcessing from './ImageProcessing';
import WebhookPayloads from './WebhookPayloads';
import InventoryDashboard from '../InventoryDashboard';

// The Uber Eats tools (add product, backup/restore, reconciliation, sync) live
// on their own Uber Tools page under the UBER EATS nav section.
type ActiveTool = 'sku' | 'image-processing' | 'inventory-dashboard' | 'webhook-payloads' | null;

const tools = [
  {
    id: 'inventory-dashboard' as const,
    title: 'Mission Control Centre',
    description:
      'Manage barcode reviews, audit missions, and stock reconciliation in one unified dashboard.',
    icon: <InventoryDashboardIcon sx={{ fontSize: 40 }} />,
    color: '#00B4FF',
  },
  {
    id: 'sku' as const,
    title: 'Next SKU Generator',
    description:
      'Fetches all catalog products, groups by SKU prefix (SKC, COMBO, etc.), and returns the next number after the current highest.',
    icon: <SkuIcon sx={{ fontSize: 40 }} />,
    color: '#1976d2',
  },
  {
    id: 'image-processing' as const,
    title: 'Image Processing',
    description:
      'Process missing product images or reprocess all images with smart trim + WebP optimization.',
    icon: <ImageProcessingIcon sx={{ fontSize: 40 }} />,
    color: '#0288d1',
  },
  {
    id: 'webhook-payloads' as const,
    title: 'Vaperite webhook payloads',
    description:
      'View, rerun, and delete the stored Vaperite menu and item webhook payloads, grouped by store.',
    icon: <WebhookIcon sx={{ fontSize: 40 }} />,
    color: '#7c4dff',
  },
];

const ToolsPage: React.FC = () => {
  const [activeTool, setActiveTool] = useState<ActiveTool>(null);
  const { settings: appSettings } = useAppSettings();
  const companionEnabled = appSettings?.enableCompanionApp ?? false;

  const visibleTools = useMemo(
    () => tools.filter(t => t.id !== 'inventory-dashboard' || companionEnabled),
    [companionEnabled]
  );

  if (activeTool === 'inventory-dashboard') {
    return <InventoryDashboard onBack={() => setActiveTool(null)} />;
  }

  if (activeTool === 'sku') {
    return <NextSkuGenerator onBack={() => setActiveTool(null)} />;
  }

  if (activeTool === 'image-processing') {
    return <ImageProcessing onBack={() => setActiveTool(null)} />;
  }

  if (activeTool === 'webhook-payloads') {
    return <WebhookPayloads onBack={() => setActiveTool(null)} />;
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200 }}>
      <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
        Tools
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Inventory management utilities
      </Typography>

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        {visibleTools.map((tool) => (
          <Card
            key={tool.id}
            variant="outlined"
            sx={{
              flex: '1 1 300px',
              maxWidth: 380,
              display: 'flex',
              flexDirection: 'column',
              transition: 'box-shadow 0.2s, transform 0.15s',
              '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' },
            }}
          >
            <CardContent sx={{ flex: 1 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: `${tool.color}14`,
                  color: tool.color,
                  mb: 2,
                }}
              >
                {tool.icon}
              </Box>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {tool.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {tool.description}
              </Typography>
            </CardContent>
            <CardActions sx={{ p: 2, pt: 0 }}>
              <Button
                variant="contained"
                size="small"
                onClick={() => setActiveTool(tool.id)}
                sx={{ bgcolor: tool.color, '&:hover': { bgcolor: tool.color } }}
              >
                Open
              </Button>
            </CardActions>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

export default ToolsPage;
